import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Calendar,CalendarClock, Image as ImageIcon, MapPin } from 'lucide-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';

import MapAddressLink from './MapAddressLink';
import EventTaskChips from './EventTaskChips';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export default function EventDetails({ event, taskLinks = [] }) {
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const start = dayjs(event.starts_at).tz(browserTimezone);
  const end = dayjs(event.ends_at).tz(browserTimezone);
  const location = event.location?.name || event.location?.address;
  const separateLocationName = event.location?.name
    && event.location.name !== event.location.address;

  return (
    <Box
      sx={(theme) => ({
        p: { xs: 2, sm: 3 },
        border: `3px solid ${theme.palette.primary.main}`,
        borderRadius: 5,
        background: `linear-gradient(145deg, ${theme.palette.background.paper}, ${alpha(theme.palette.primary.main, 0.08)})`,
        boxShadow: `0 9px 0 ${alpha(theme.palette.primary.main, 0.2)}`,
      })}
    >
      <Stack spacing={2.25}>
        <Typography variant="h4" align="center" sx={{ color: 'primary.main' }}>{event.title}</Typography>
        <Box
          data-testid="event-image-frame"
          data-aspect-ratio="square"
          style={{ aspectRatio: '1 / 1' }}
          sx={(theme) => ({
            width: '100%',
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
            border: `3px solid ${theme.palette.secondary.main}`,
            borderRadius: 4,
            bgcolor: alpha(theme.palette.secondary.main, 0.08),
            boxShadow: `0 0 20px ${alpha(theme.palette.secondary.main, 0.18)}`,
          })}
        >
          {event.image?.url ? (
            <Box
              component="img"
              src={event.image.url}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Stack alignItems="center" sx={{ color: 'secondary.main' }}>
              <ImageIcon size={42} />
            </Stack>
          )}
        </Box>
          {event.description && (
          <Typography variant="body2" sx={{ color: 'primary.main', textAlign: 'center', whiteSpace: 'pre-wrap' }}>
            {event.description}
          </Typography>
        )}
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <Calendar size={20} />
          <Box>

            <Typography variant="body1" sx={{ color: 'primary.main', textAlign: 'left' }}>
              {start.format('dddd, MMMM D')}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1.25} alignItems="flex-start">
          <CalendarClock size={20} />
          <Box>

            <Typography variant="body1" sx={{ color: 'primary.main', textAlign: 'left' }}>
              {start.format('h:mm A')} – {end.format('h:mm A')}
            </Typography>
          </Box>
        </Stack>
        {location && (
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <MapPin size={20} />
            <Stack spacing={0.25} alignItems="flex-start">
              {separateLocationName && (
                <Typography variant="body1" sx={{ color: 'primary.main', textAlign: 'left' }}>
                  {event.location.name}
                </Typography>
              )}
              {event.location.address ? (
                <MapAddressLink location={event.location} />
              ) : (
                <Typography variant="body1" sx={{ color: 'secondary.main', textAlign: 'left' }}>
                  {event.location.name}
                </Typography>
              )}
            </Stack>
          </Stack>
        )}
        <EventTaskChips links={taskLinks} />
        {event.organizer && (
          <Typography variant="caption" sx={{ color: 'secondary.main', textAlign: 'left' }}>
            Created by {event.organizer}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
